// Move-pallet correction logic, lifted out of MovePalletDialog so the dialog is
// composition and the decisions are testable on their own (mig 810 + 820).
//
// The dialog moves pallet(s) to a different run of the SAME commodity — the
// admin fix for "packed on the wrong run", including the wrong-VARIETY run,
// which is how ops actually change a pallet's variety. Two modes share the RPC
// pair: `single` carries the pallet's version for the optimistic-concurrency
// check, `bulk` is all-or-nothing server-side.

import type { QueryClient } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'

export type MoveMode =
  | { kind: 'single'; palletId: string; version: number }
  | { kind: 'bulk';   palletIds: string[] }

/** How many pallets the move covers — drives every count/plural in the UI. */
export function moveCount(mode: MoveMode): number {
  return mode.kind === 'single' ? 1 : mode.palletIds.length
}

/** The move is submittable: a target picked, a reason typed, and — when the
 *  selection includes dispatched/consigned stock — the force box ticked. */
export function canSubmitMove(args: {
  targetRunId: string | null
  reason: string
  requiresForce: boolean
  forceConfirmed: boolean
}): boolean {
  const { targetRunId, reason, requiresForce, forceConfirmed } = args
  if (!targetRunId) return false
  if (reason.trim().length === 0) return false
  return !requiresForce || forceConfirmed
}

/** True when the chosen destination run carries a different variety — the move
 *  is still allowed (this is how ops correct a variety), but the pallet's PPECB
 *  variety_code is re-snapshotted from the target, so it must be surfaced. */
export function varietyChanges(
  target: { variety: string | null } | null,
  currentVariety: string | null,
): boolean {
  if (!target || !currentVariety) return false
  return target.variety !== currentVariety
}

/** Dirty baseline (K-forms #5) — every field, synchronous, so a backdrop tap
 *  can never silently discard a typed reason mid-correction. */
export function moveSnapshot(args: {
  targetRunId: string | null
  reason: string
  forceConfirmed: boolean
}): string {
  return `${args.targetRunId ?? ''}|${args.reason}|${args.forceConfirmed}`
}

/** Fire the right RPC for the mode. Throws the PostgREST error unchanged so the
 *  caller's onError can still see an optimistic-lock failure for what it is. */
export async function runMoveRpc(
  db: Pick<SupabaseClient, 'rpc'>,
  args: {
    mode: MoveMode
    targetRunId: string | null
    reason: string
    requiresForce: boolean
  },
): Promise<void> {
  const { mode, targetRunId, reason, requiresForce } = args
  if (!targetRunId) throw new Error('Pick a target run')
  const p_reason = reason.trim() || null

  const { error } = mode.kind === 'single'
    ? await db.rpc('move_pallet_to_run', {
      p_pallet_id:      mode.palletId,
      p_new_run_id:     targetRunId,
      p_pallet_version: mode.version,
      p_reason,
      p_force:          requiresForce,
    })
    : await db.rpc('move_pallets_to_run', {
      p_pallet_ids: mode.palletIds,
      p_new_run_id: targetRunId,
      p_reason,
      p_force:      requiresForce,
    })

  if (error) throw error
}

/** Query keys invalidated after a successful move. BOTH runs' aggregates moved
 *  (mig 639 trigger), so this is deliberately broad. */
export function moveInvalidationKeys(mode: MoveMode): unknown[][] {
  const keys: unknown[][] = [['runs'], ['run-pallets'], ['pallets']]
  if (mode.kind === 'single') {
    keys.push(['pallet-detail', mode.palletId], ['pallet-lines', mode.palletId])
  }
  return keys
}

export function invalidateAfterMove(qc: Pick<QueryClient, 'invalidateQueries'>, mode: MoveMode): void {
  for (const queryKey of moveInvalidationKeys(mode)) qc.invalidateQueries({ queryKey })
}
